/**
 * 🐑 **What the aim line says about the animal under the crosshair** — T286.
 *
 * Pure, so a test can reach it: `main.ts` draws whatever this returns in the
 * middle of the screen, above the bars (`hud-scene.ts` `AIM_Y`).
 *
 * 🐣 **A lamb says it is a lamb** — 🎯 haugur, 2026-10-02: *"lambs register as
 * sheep when i look at them."* The wire's `lamb` is present only on a lamb
 * (protocol 44), so its presence is the whole test.
 *
 * 🦊 **And a fox says it is a fox** — 🎯 haugur, 2026-10-05: *"when i look at a
 * fox it does not say it in Looking at."* 🔴 The line only knew animals you
 * could pick up, so it looked straight through a fox and named the grass.
 */

import { FOX_KIND, t } from '@lofi/shared';
import type { AimTone } from './hud-scene.ts';

export interface AimLine {
  readonly target: string;
  readonly targetTone: AimTone;
}

/** What the last `creatures` message said about the animal — the parts read here. */
export interface SeenAnimal {
  readonly kind: string;
  readonly doing?: string | null | undefined;
  /** 🦊 True while a fox has a lamb in its jaws — protocol 51. */
  readonly prey?: boolean | undefined;
  readonly hunger?: string | undefined;
  readonly lamb?: number | undefined;
}

/**
 * @param seen the creature as the last `creatures` message described it.
 * @param carriable whether `C` would pick it up right now — in arm's reach,
 *   and your arms empty.
 * @returns the line, or `null` for a kind this does not know — the caller then
 *   names the block instead.
 */
export function animalAimLine(seen: SeenAnimal | undefined, carriable: boolean): AimLine | null {
  if (seen === undefined) return null;

  if (seen.kind === FOX_KIND) {
    /* 🤫 Asleep is the one moment a fox can be taken — decision 134 — so the
       line teaches the hunt, the way "press C" teaches carrying. */
    if (seen.doing === 'Sleep') return { target: t('hud.target.foxAsleep'), targetTone: 'plain' };
    if (seen.prey === true) return { target: t('hud.target.foxWithLamb'), targetTone: 'hungry' };
    return { target: t('hud.target.fox'), targetTone: 'plain' };
  }

  /* 🐑 `kind` is Icelandic for a sheep — `carry.ts` explains the name. */
  if (seen.kind !== 'kind') return null;
  const young = seen.lamb !== undefined;
  if (seen.hunger === 'hungry')
    return {
      target: t(young ? 'hud.target.hungryLamb' : 'hud.target.hungry'),
      targetTone: 'hungry',
    };
  if (seen.hunger === 'starving')
    return {
      target: t(young ? 'hud.target.starvingLamb' : 'hud.target.starving'),
      targetTone: 'starving',
    };
  if (carriable)
    return {
      target: t(young ? 'hud.target.carriableLamb' : 'hud.target.carriable'),
      targetTone: 'plain',
    };
  return { target: t(young ? 'hud.target.lamb' : 'hud.target.sheep'), targetTone: 'plain' };
}
